import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { OfficerService, ShiftService } from "@services";
import utils from "@utils";
import { AsyncSelect, Modal, Image } from "../../../components";

let cancelOfficerSearch;
class AddEditShiftModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			name: "",
			supervisorId: "",
			supervisorName: "",
			officerResults: [],
			assignedOfficers: [],
			errors: {
				name: false,
				supervisorId: false,
			},
		};
	}

	componentDidUpdate({ show: prevShow }) {
		const { show, editId } = this.props;
		if (prevShow !== show && show && editId) {
			this.getShiftDetails();
		}
	}

	/** Retreives the details of a specific shift */
	getShiftDetails = () => {
		const { lang } = global;
		const { editId } = this.props;
		ShiftService.getById(editId)
			.then(({ data }) => {
				this.setState({
					assignedOfficers: data.Officers,
					name: data.Name,
					supervisorId: data.SupervisorId,
					supervisorName: data.SupervisorName,
				});
			})
			.catch(() =>
				utils.notify({
					type: "error",
					message: lang.t("Error retrieving shift details"),
					timeOut: 2000,
				}),
			);
	};

	handleChange = property => event => {
		let value = "";
		if (property === "name")
			({
				target: { value },
			} = event);
		else if (event) ({ value } = event);

		this.setState({ [property]: value });
	};

	/**
	 * Retrieves a list of officers based on the search value
	 * @param {String} input The value being searched for
	 * @param {Function} callback A callback method used to trigger updates to a a list of officers
	 */
	loadOfficers = (input, callback) => {
		const { lang } = global;
		if (input.length < 3) {
			callback([]);
			return;
		}

		if (cancelOfficerSearch) cancelOfficerSearch();

		const queryParams = { vettedStatus: 2 };
		const data = { FirstName: input, LastName: input };
		const getCancelToken = c => {
			cancelOfficerSearch = c;
		};

		OfficerService.search(queryParams, data, getCancelToken)
			.then(({ data: { ItemList } }) => {
				callback(ItemList.map(({ Id: value, ContactName: label }) => ({ value, label })));
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading officers"),
					});
					callback([]);
				}
			});
	};

	handleSupervisorChange = event => {
		this.setState({ supervisorId: event?.value || "", supervisorName: event?.label || "" });
	};

	/** Retreives a list of officers which match the search value */
	searchOfficers = ({ target: { value: input } }) => {
		const { lang } = global;
		if (input.length < 3) {
			this.setState({ officerResults: [] });
			return;
		}

		const queryParams = { vettedStatus: 2, returnOfficersNotInAShift: true };
		const data = { FirstName: input, LastName: input };
		const getCancelToken = c => {
			cancelOfficerSearch = c;
		};

		OfficerService.search(queryParams, data, getCancelToken)
			.then(({ data: { ItemList } }) => this.setState({ officerResults: ItemList }))
			.catch(error => {
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: lang.t("Error loading officers"),
					});
					this.setState({ officerResults: [] });
				}
			});
	};

	/**
	 * Allows for assigning an officer to a shift
	 * @param {Object} officer The officer being assigned to the shift
	 */
	updateAssignment = officer => {
		this.setState(prevState => {
			const { assignedOfficers } = prevState;
			if (assignedOfficers.some(({ Id }) => Id === officer.Id))
				return { assignedOfficers: assignedOfficers.filter(({ Id }) => Id !== officer.Id) };
			return { assignedOfficers: [...prevState.assignedOfficers, officer] };
		});
	};

	/** Gathers all required information required for updating or adding shift */
	onSubmit = () => {
		const { lang } = global;
		const { name: Name, supervisorId: SupervisorId, assignedOfficers } = this.state;
		const { editId, onSuccess } = this.props;
		const errors = {};
		errors.name = Name.trim() === "";
		errors.supervisorId = SupervisorId === "";
		this.setState({ errors });
		if (errors.name || errors.supervisorId) return;
		const Officers = assignedOfficers.map(({ Id }) => Id);
		const data = { Name, SupervisorId, Officers };

		if (editId) {
			// Editing existing shift
			data.Id = editId;
			ShiftService.edit(data)
				.then(() => onSuccess())
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error updating shift"),
						timeOut: 2000,
					}),
				);
		} else {
			// Creating new shift
			ShiftService.add(data)
				.then(() => onSuccess())
				.catch(() =>
					utils.notify({
						type: "error",
						message: lang.t("Error creating shift"),
						timeOut: 2000,
					}),
				);
		}
	};

	onExited = () =>
		this.setState({
			name: "",
			supervisorId: "",
			supervisorName: "",
			officerResults: [],
			assignedOfficers: [],
			errors: {
				name: false,
				supervisorId: false,
			},
		});

	render() {
		const { lang } = global;
		const { name, supervisorId, supervisorName, officerResults, assignedOfficers, errors } =
			this.state;
		const { show, onHide, editId } = this.props;
		const editMode = editId !== undefined;

		return (
			<Modal
				show={show}
				id="shift-modal"
				bsSize="lg"
				onHide={onHide}
				onExited={this.onExited}
			>
				<Modal.Header closeButton>
					<Modal.Title>{lang.t(editMode ? "Edit Shift" : "New Shift")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<div className="col-sm-6">
							<div className="form-group">
								<label
									htmlFor="shift-name"
									className={errors.name ? "text-danger" : ""}
								>
									{lang.t("Shift Name")}
								</label>
								<input
									id="shift-name"
									name="shift-name"
									className={`form-control ${errors.name ? "border-danger" : ""}`}
									value={name}
									onChange={this.handleChange("name")}
								/>
							</div>
							<div className="form-group">
								<label
									htmlFor="shift-supervisor"
									className={errors.supervisorId ? "text-danger" : ""}
								>
									{lang.t("Supervisor")}
								</label>
								<AsyncSelect
									placeholder={lang.t("Search")}
									className={errors.supervisorId ? "border-danger" : ""}
									cacheOptions
									isClearable
									loadOptions={this.loadOfficers}
									onChange={this.handleSupervisorChange}
									value={{ value: supervisorId, label: supervisorName }}
								/>
							</div>
							<h4>{lang.t("Assign Officers to Shift")}</h4>
							<div className="form-group">
								<input
									name="officer-search"
									className="form-control"
									placeholder="Search for officers by first or last name"
									onChange={this.searchOfficers}
								/>
							</div>
							<div className="results-section">
								<If condition={officerResults.length === 0}>
									<div className="text-center">
										<em>{lang.t("Search results will appear here")}</em>
									</div>
								</If>
								<For
									each="officer"
									of={officerResults}
								>
									<div
										className="media"
										key={`officer-${officer.Id}`}
									>
										<div className="media-left media-middle">
											<Image
												className="img-circle media-object"
												src={officer.ContactPhotoUrl}
												fallbackSrc="avatar"
											/>
										</div>
										<div className="media-body media-middle">
											{officer.ContactName}
											<If condition={officer.Id === supervisorId}>
												<span className="label label-primary">
													{lang.t("Supervisor")}
												</span>
											</If>
											<br />
											<small>{officer.OfficerRank}</small>
										</div>
										<div className="media-right media-middle">
											<Choose>
												<When
													condition={
														!assignedOfficers.some(
															({ Id }) => Id === officer.Id,
														)
													}
												>
													<button
														type="button"
														className="btn btn-link"
														onClick={() =>
															this.updateAssignment(officer)
														}
													>
														<i className="rkf rkf-plus" />
													</button>
												</When>
												<Otherwise>
													<i className="rkf rkf-check-mark" />
												</Otherwise>
											</Choose>
										</div>
									</div>
								</For>
							</div>
						</div>
						<div className="col-sm-6 assigned-officers">
							<h4>{lang.t("Assigned Officers")}</h4>
							<For
								each="assigned"
								of={assignedOfficers}
							>
								<div
									className="media"
									key={`assigned-${assigned.Id}`}
								>
									<div className="media-left media-middle">
										<Image
											className="img-circle media-object"
											src={assigned.ContactPhotoUrl}
											fallbackSrc="avatar"
										/>
									</div>
									<div className="media-body media-middle">
										{assigned.ContactName}
										<If condition={assigned.Id === supervisorId}>
											<span className="label label-primary">
												{lang.t("Supervisor")}
											</span>
										</If>
										<br />
										<small>{assigned.OfficerRank}</small>
									</div>
									<div className="media-right media-middle">
										<button
											type="button"
											className="btn btn-link"
											onClick={() => this.updateAssignment(assigned)}
										>
											<i className="rkf rkf-cross-solid text-danger" />
										</button>
									</div>
								</div>
							</For>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-primary"
						onClick={this.onSubmit}
					>
						{lang.t(editMode ? "Update Shift" : "Create Shift")}
					</button>
				</Modal.Footer>
			</Modal>
		);
	}
}

AddEditShiftModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	editId: PropTypes.number,
	onSuccess: PropTypes.func,
};

AddEditShiftModal.defaultProps = {
	show: false,
	onHide: () => {},
	editId: undefined,
	onSuccess: () => {},
};

export default AddEditShiftModal;
